/**
 * Product-notification signup confirmation.
 *
 * When a visitor joins the product-pipeline "notify me" list and the row is
 * persisted to `marketing_submissions` (segment `product_notification`), we ping
 * the `notify-subscribe` edge function so it emails the subscriber a "you're on
 * the list" confirmation via Resend. See
 * `supabase/functions/notify-subscribe/index.ts`.
 *
 * Like `captureLead`, this is fire-and-forget: the signup is already saved, so a
 * missing backend or a mail failure must never block the form's success state.
 * The edge function confirms the persisted signup row exists (its anti-spam
 * gate), so this only carries the email that keys that lookup.
 */

import { supabase, isSupabaseConfigured } from "../lib/supabaseClient";

const FUNCTION = "notify-subscribe";

/**
 * Ask the backend to email a new subscriber their signup confirmation.
 *
 * @param {Object} params
 * @param {string} params.email The email that joined the list.
 * @returns {Promise<{ error: (Error|null) }>}
 */
export async function notifySubscribe({ email } = {}) {
  if (!email) {
    return { error: new Error("notifySubscribe: email is required") };
  }

  if (!isSupabaseConfigured) {
    return { error: null };
  }

  try {
    const { error } = await supabase.functions.invoke(FUNCTION, {
      body: {
        email,
        appUrl: typeof window !== "undefined" ? window.location.origin : undefined,
      },
    });
    if (error) {
      // eslint-disable-next-line no-console
      console.warn("notify-subscribe failed", error.message || error);
    }
    return { error: error || null };
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn("notify-subscribe threw", err);
    return { error: err };
  }
}
